<template>
  <div class="personal-details p-4 md:p-6 flex flex-col md:flex-row">
    <!-- profile card: who you are and which vaults you are in -->
    <div class="bg-grey-lightest rounded p-4 md:w-1/4 md:mr-6 flex-none">
      <div class="flex items-center">
        <div
          class="h-12 w-12 flex-none bg-brand flex items-center justify-center rounded-full text-white text-xl font-medium"
        >
          {{ oneCharName(user) }}
        </div>
        <p class="ml-3 font-medium text-lg leading-tight break-words min-w-0">
          {{ user.first_name }} {{ user.last_name }}
        </p>
      </div>

      <p class="mt-4 pt-3 border-t border-grey-light text-xxs uppercase tracking-wide text-grey-dark font-semibold">
        {{ vaults.length }} {{ vaults.length == 1 ? "vault" : "vaults" }}
      </p>
      <div class="mt-1">
        <div
          v-for="vault in vaults"
          :key="vault.id"
          class="flex items-center justify-between py-2 border-b border-grey-light text-sm"
        >
          <span class="min-w-0 break-words pr-2">
            <i class="text-brand mr-1" :class="vault_type == 'business' ? 'fa fa-briefcase' : 'fa fa-building'"></i>
            {{ vault.name }}
          </span>
          <button
            v-if="vault.user_id != user.id"
            type="button"
            class="flex-none border border-red bg-white text-red text-xs font-medium py-1 px-2 rounded hover:bg-red hover:text-white"
            @click.prevent="confirmLeave(vault)"
          >
            Leave
          </button>
        </div>
      </div>
    </div>

    <form class="flex-1 min-w-0 mt-6 md:mt-0" @submit.prevent="updateUser">
      <!-- heading with the one action on the right, like Vault Details -->
      <div class="mb-1 pb-1 border-b flex items-end justify-between">
        <p class="text-lg">
          <i class="fa fa-user text-brand"></i> Personal Details
        </p>
        <button
          type="submit"
          class="mb-1 border border-brand bg-brand text-white text-sm font-medium py-2 px-4 rounded hover:bg-brand-darkest"
          :class="{ 'cursor-not-allowed opacity-75': updating }"
          :disabled="updating"
        >
          <i class="fa mr-1" :class="updating ? 'fa-spin fa-spinner' : 'fa-save'"></i>
          {{ updating ? "Updating" : "Update" }}
        </button>
      </div>
      <div class="flex flex-wrap">
        <div class="w-full py-1 md:w-1/2 md:pr-8">
          <label class="font-medium" for="first_name">First Name</label>
          <input
            id="first_name"
            type="text"
            class="w-full border py-2 px-3 mt-1 rounded"
            v-model="edit_user.first_name"
            v-validate="'required|max:255'"
            name="first_name"
            :class="{ 'border-red': errors.has('first_name') }"
          />
          <p class="text-red text-sm mt-1" v-if="errors.has('first_name')">{{ errors.first("first_name") }}</p>
        </div>
        <div class="w-full py-1 md:w-1/2">
          <label class="font-medium" for="last_name">Last Name</label>
          <input
            id="last_name"
            type="text"
            class="w-full border py-2 px-3 mt-1 rounded"
            v-model="edit_user.last_name"
            v-validate="'required|max:255'"
            name="last_name"
            :class="{ 'border-red': errors.has('last_name') }"
          />
          <p class="text-red text-sm mt-1" v-if="errors.has('last_name')">{{ errors.first("last_name") }}</p>
        </div>
        <div class="w-full py-1 md:w-1/2 md:pr-8">
          <label class="font-medium" for="email">Email</label>
          <input
            id="email"
            type="text"
            class="w-full border py-2 px-3 mt-1 rounded"
            v-model="edit_user.email"
            v-validate="'required|email|max:255'"
            name="email"
            :class="{ 'border-red': errors.has('email') }"
          />
          <p class="text-red text-sm mt-1" v-if="errors.has('email')">{{ errors.first("email") }}</p>
        </div>
        <div class="w-full py-1 md:w-1/2">
          <label class="font-medium" for="mobile_number">Mobile Number</label>
          <input id="mobile_number" type="text" class="w-full border py-2 px-3 mt-1 rounded" v-model="edit_user.mobile_number" />
        </div>
      </div>

      <p class="mt-6 mb-1 pb-1 border-b text-lg">
        <i class="fa fa-address-card text-brand"></i> Address Details
      </p>
      <div class="flex flex-wrap">
        <div class="w-full py-1 md:w-1/2 md:pr-8">
          <label class="font-medium" for="company_name">Company Name</label>
          <input id="company_name" type="text" class="w-full border py-2 px-3 mt-1 rounded" v-model="edit_user.company_name" />
        </div>
        <div class="w-full py-1 md:w-1/2">
          <label class="font-medium" for="address">Address</label>
          <input id="address" type="text" class="w-full border py-2 px-3 mt-1 rounded" v-model="edit_user.address" />
        </div>
        <div class="w-full py-1 md:w-1/3 md:pr-8">
          <label class="font-medium">Country</label>
          <multiselect
            class="mt-1"
            v-model="edit_user.country"
            name="country"
            placeholder="Please Select"
            :options="countries"
            :showLabels="false"
          ></multiselect>
        </div>
        <div class="w-full py-1 md:w-1/3 md:pr-8">
          <label class="font-medium">{{ isNZ ? "Type" : "State" }}</label>
          <multiselect
            class="mt-1"
            v-model="edit_user.state"
            name="state"
            placeholder="Please Select"
            :options="states"
            :showLabels="false"
          ></multiselect>
        </div>
        <div class="w-full py-1 md:w-1/3">
          <label class="font-medium" for="city">City</label>
          <input id="city" type="text" class="w-full border py-2 px-3 mt-1 rounded" v-model="edit_user.city" />
        </div>
      </div>

      <p class="mt-6 mb-1 pb-1 border-b text-lg">
        <i class="fa fa-user-lock text-brand"></i> Security
      </p>
      <div class="flex items-start py-2">
        <toggle-button
          class="flex-none mr-3 mt-1"
          :value="!!edit_user.two_factor_enabled"
          :sync="true"
          :labels="false"
          :width="40"
          :height="22"
          :color="toggle_colors"
          @change="edit_user.two_factor_enabled = $event.value"
        ></toggle-button>
        <label class="block cursor-pointer" @click="edit_user.two_factor_enabled = !edit_user.two_factor_enabled">
          <span class="font-medium block">Two factor authentication</span>
          <span class="text-xs text-grey-darker block mt-1">
            A code is sent to you each time you sign in. Press Update to save the change.
          </span>
        </label>
      </div>
    </form>

    <confirm-dialog
      ref="leave_vault_confirm"
      title="Leave this vault?"
      :body="leaveBody"
      action-label="Leave vault"
      @confirm="leaveVault"
    ></confirm-dialog>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import ConfirmDialog from "../../components/ConfirmDialog.vue";

export default {
  components: { ConfirmDialog },

  data() {
    return {
      edit_user: {},

      updating: false,

      countries: ["Australia", "New Zealand"],

      leave_pending: null,
      toggle_colors: { checked: "#4296d4", unchecked: "#B8BECC" },
    };
  },

  mounted() {
    if (_.isEmpty(this.edit_user)) {
      this.edit_user = _.cloneDeep(this.user);
    }
  },

  methods: {
    oneCharName(user) {
      if (user.first_name) {
        return user.first_name.substring(0, 1);
      }

      if (user.last_name) {
        return user.last_name.substring(0, 1);
      }

      return "-";
    },

    updateUser() {
      this.$validator.reset();
      this.$validator.validateAll().then((result) => {
        if (result) {
          this.updating = true;
          axios
            .patch("api/users/profile", {
              first_name: this.edit_user.first_name,
              last_name: this.edit_user.last_name,
              email: this.edit_user.email,
              mobile_number: this.edit_user.mobile_number,
              company_name: this.edit_user.company_name,
              country: this.edit_user.country,
              state: this.edit_user.state,
              city: this.edit_user.city,
              address: this.edit_user.address,
              two_factor_enabled: this.edit_user.two_factor_enabled,
            })
            .then(({ data }) => {
              this.updating = false;
              if (data.success) {
                this.$noty.success("Profile updated", { killer: true, timeout: 2000 });
                this.edit_user = _.cloneDeep(data.user);
                this.$store.commit("setUser", data.user);
              }
            })
            .catch(({ response }) => {
              this.updating = false;
              if (_.size(response.data.errors) > 0) {
                _.each(response.data.errors, (error_msg, field) => {
                  this.errors.add(field, _.toString(error_msg));
                });
              } else if (!_.isNull(response.data.message)) {
                this.$noty.error(response.data.message, { killer: true, timeout: 4000 });
              }
            });
        }
      });
    },

    confirmLeave(vault) {
      this.leave_pending = vault;
      this.$refs.leave_vault_confirm.open();
    },

    leaveVault() {
      const vault = this.leave_pending;
      axios
        .delete(`api/vaults/${vault.id}/users/leave`)
        .then(({ data }) => {
          if (data.success) {
            this.$noty.success(`You left ${vault.name}`, { killer: true, timeout: 3000 });
            this.$ebus.$emit("refresh-vaults");
          } else {
            this.$noty.error(data.message || "You could not leave the vault", { killer: true, timeout: 4000 });
          }
        })
        .catch(() => {
          this.$noty.error("You could not leave the vault", { killer: true, timeout: 4000 });
        })
        .finally(() => {
          this.leave_pending = null;
        });
    },
  },

  computed: {
    ...mapGetters({
      user: "getUser",
      vaults: "getVaults",
    }),

    vault_type() {
      return window.vault_type;
    },

    states() {
      if (this.edit_user.country == "New Zealand") {
        return [
          "Body Corporate",
          "Residents Association",
          "Incorporated Society",
          "Cross Lease (XL)"
        ];
      }

      return [
          "Australian Capital Territory",
          "New South Wales",
          "Northern Territory",
          "Queensland",
          "South Australia",
          "Tasmania",
          "Victoria",
          "Western Australia",
      ];
    },

    isNZ() {
      return this.edit_user.country == "New Zealand";
    },

    leaveBody() {
      const name = this.leave_pending ? this.leave_pending.name : "this vault";
      return `You lose access to ${name} until someone invites you again.`;
    },
  },
};
</script>

<style>
/* Same 42px box as the text inputs on this page, like Vault Details */
.personal-details .multiselect {
  min-height: 42px;
}
.personal-details .multiselect__tags {
  min-height: 42px;
  height: 42px;
  padding: 9px 40px 0 12px;
  border-color: #dadee7;
}
.personal-details .multiselect__select {
  height: 40px;
}
</style>
